import { useCallback, useMemo } from 'react';

import moment from 'moment';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { useAccount } from 'wagmi';

import { updateLoanMemberAddress } from '@/api/loans';
import MoreIcon from '@/assets/icons/elipses.svg';
import NumberWithTooltip from '@/components/shared/tooltips/NumberTooltip';
import { useDisplayLTV } from '@/hooks/loans/useDisplayLTV';
import { useAppSelector } from '@/hooks/redux';
import { setLoanDefaulted } from '@/redux/loans/loansSlice';
import { IFloorPriceLoan, ILoanUser, IMyLoan, TStatusLoan } from '@/redux/loans/model';
import { displayRepay } from '@/utils/formatter';
import { AWSImageConverter, cn } from '@/utils/helper';
import { ICollectionDetail } from 'simplehash_api/types/simpleHash';
import { useNettyContract } from 'smart-contracts/contracts';

import TableUi from '../../top-sales/TableUi';
import { TOnCancel } from '../CancelButton';
import NettyScore from '../NettyScore';
import Duration, { rowBaseClassName } from './Duration';
import OpenOfferLendAcception from './OpenOfferLendAcception';
import RepayLoan from '../RepayLoan';

export interface IMyLoansTableData extends Omit<IMyLoan, 'loanChainId' | 'status' | 'acceptedAt' | 'lenderAddress' | 'loanRequest' | 'endDate'> {
  loanChainId?: string;
  status?: TStatusLoan;
  user?: ILoanUser;
  borrower?: ILoanUser;
  endDate?: string;
  loanRequest: Omit<IMyLoan['loanRequest'], 'loanTokenOwner' | 'tokenId' | 'totalAmountBorrower' | 'totalAmountLender' | 'user'> & {
    loanTokenOwner?: Partial<IMyLoan['loanRequest']['loanTokenOwner']>;
    collection: ICollectionDetail & Partial<IFloorPriceLoan>;
    collectionId?: string;
    tokenId: string | null;
    totalAmountBorrower?: number;
    totalAmountLender?: number;
    user?: ILoanUser;
  };
}

interface IMyLoansTable {
  columns: any[];
  data: IMyLoansTableData[];
  isLent?: boolean;
  lenderWithPicture?: boolean;
  sortKey?: string;
  dirKey?: string;
  onCancel?: TOnCancel;
  trStyle?: string;
  thStyle?: string;
  tdStyle?: string;
  wrapperStyle?: string;
  disableNavigate?: boolean;
}

const forclosedStatusClass = '!bg-rose/15 !text-rose';

const statusStyles = {
  active: { class: '!bg-primary/15 !text-primary-blue-100', label: 'open' },
  paid: { class: '!bg-forest-green/15 !text-forest-green', label: 'Paid' },
  foreclosed: { class: forclosedStatusClass, label: 'Defaulted' },
  unpaid: {
    class: forclosedStatusClass,
    label: 'Defaulted',
  },
};

interface IProfileColumnProps {
  user: ILoanUser;
}

const ProfileColumn: React.FC<IProfileColumnProps> = ({ user }) => {
  return (
    <div className='flex gap-[14px] pl-6 w-max items-center'>
      <img src={user.profile_picture ? AWSImageConverter(user.profile_picture) : '/avatar-placeholder.png'} className='size-9 rounded object-cover' alt='loan image' />
      <div className='flex flex-col'>
        <p className='w-max font-medium text-[10px]/3 lg:text-xs'>{user.name}</p>
      </div>
    </div>
  );
};

const MyLoansTabTable: React.FC<IMyLoansTable> = ({ columns, data, isLent, lenderWithPicture = true, sortKey, dirKey, wrapperStyle, onCancel, trStyle, thStyle, disableNavigate = false, tdStyle }) => {
  const router = useRouter();
  const nettyContract = useNettyContract();
  const dispatch = useDispatch();
  const userAccount = useAppSelector((state) => state.user.userDetail);
  const { address } = useAccount();

  const forcloseLoan = useCallback(
    async (loanChainId: string, id: number) => {
      if (!address) return;
      await updateLoanMemberAddress({ id, address: 'borrowerAddress' });

      const forclosed = await nettyContract.functions.forCloseLoan(loanChainId);

      await toast.promise(forclosed.wait(), {
        error: 'Something went wrong',
        loading: 'Please wait until the claim is finished.',
        success: 'The loan has been successfully foreclosed.',
      });

      dispatch(setLoanDefaulted(loanChainId));
    },
    [dispatch, nettyContract.functions, address]
  );

  const displayLTV = useDisplayLTV();

  const tableData = useMemo(() => {
    return data.map((loan) => {
      const {
        status,
        id,
        loanRequest: { apr, tokenId, principal, loanTokenOwner, repay, collection, term, token, expiryDate },
        loanChainId,
        user,
        borrower,
        endDate,
      } = loan;

      const expirationDate = moment(endDate);
      const isUnpaid = status === 'active' && expirationDate.isSameOrBefore(moment());
      const baseClass = rowBaseClassName(isUnpaid);
      const isValidDate = 'loanRequest' in loan && moment().isBefore(moment(loan.endDate)) && status === 'active';

      const tableObject: {
        collection: JSX.Element;
        apr: JSX.Element;
        ltv?: JSX.Element;
        amount: JSX.Element;
        repay: JSX.Element;
        due: JSX.Element;
        more: JSX.Element;
        collection_id: {
          status: TStatusLoan | undefined;
          id: number;
          tokenId: string | null;
          collectionId: string;
        };
        status?: JSX.Element;
        lender?: JSX.Element;
        borrower?: JSX.Element;
        duration?: JSX.Element;
        nettyScore?: JSX.Element;
        expires?: JSX.Element;
        acceptOrCancel?: JSX.Element;
        repayButton: JSX.Element;
      } = {
        collection: (
          <div className='flex items-center gap-[14px] pl-6 w-max pr-1'>
            <img src={loanTokenOwner?.image || collection?.image || '/bayc-collectors.png'} className='size-9 rounded object-cover' alt='loan image' />
            <div className='flex flex-col'>
              <p className='w-max font-medium text-base'>{loanTokenOwner ? loanTokenOwner.collectionName : 'Collection Offer'}</p>
              <p className='w-max font-normal text-xs'>{tokenId ? `#${tokenId}` : collection?.name}</p>
            </div>
          </div>
        ),
        due: <Duration expirationDate={endDate ?? 0} isUnpaid={isUnpaid} status={status} />,
        apr: (
          <NumberWithTooltip fullNumber={apr} symbol='%' wrapperClassName='w-full'>
            <p className={cn('w-max mx-auto', baseClass)}>{displayRepay(apr)}%</p>
          </NumberWithTooltip>
        ),
        ltv: (
          <NumberWithTooltip fullNumber={displayLTV(principal, loanTokenOwner ? loanTokenOwner?.floorPrice : collection?.floorPrice, token?.symbol)} symbol='' wrapperClassName='w-full'>
            <p className={cn('w-max mx-auto', baseClass)}>{displayLTV(principal, loanTokenOwner ? loanTokenOwner?.floorPrice : collection?.floorPrice, token?.symbol)}</p>
          </NumberWithTooltip>
        ),
        amount: (
          <NumberWithTooltip fullNumber={principal} symbol={token?.symbol} wrapperClassName='w-full'>
            <p className={cn('w-max mx-auto', baseClass)}>
              {displayRepay(principal)} {token?.symbol}
            </p>
          </NumberWithTooltip>
        ),
        repay: (
          <NumberWithTooltip fullNumber={repay} symbol={token?.symbol} wrapperClassName='w-full'>
            <p className={cn('w-max mx-auto', baseClass)}>
              {displayRepay(displayRepay(repay))} {token?.symbol}
            </p>
          </NumberWithTooltip>
        ),
        repayButton: (isValidDate && token && loanChainId && <RepayLoan className='!w-[80px] !h-[39px] !text-xs/3 lg:!text-xs/3 m-auto' currency={token.address} loanChainId={loanChainId} repayNative={repay} loanId={id} />) || <></>,

        more: (
          <div className='flex items-center gap-7'>
            {isUnpaid && isLent && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  forcloseLoan(loanChainId || '', id);
                }}
                className='bg-primary text-white text-xs/3 font-semibold w-[77px] h-[39px] rounded flex justify-center items-center'
              >
                Claim
              </button>
            )}
            <button className='w-[3px] h-3 mr-4 !bg-transparent'>
              <MoreIcon className='[&_path]:fill-coal-250 group-hover:[&_path]:fill-primary' />
            </button>
          </div>
        ),
        collection_id: { status, id, tokenId, collectionId: collection?.id },
        duration: <p className={cn('w-max mx-auto', baseClass)}>{term} Days</p>,
        expires: <p className={cn('w-max mx-auto', baseClass)}>{moment(expiryDate).format('MM.DD.YYYY')} </p>,
      };

      if (loan.loanRequest.user) {
        tableObject.acceptOrCancel = <OpenOfferLendAcception onCancel={onCancel} loanRequest={{ ...loan.loanRequest, user: loan.loanRequest.user }} />;
      }

      if (status && loanChainId) {
        const statusFront = isUnpaid ? 'unpaid' : status;
        const currentStatus = statusStyles[statusFront];

        tableObject.status = <div className={cn('text-[10px]/3 lg:text-sm font-medium justify-center w-24 h-[33px] rounded flex items-center', currentStatus.class)}>{currentStatus.label}</div>;

        tableObject.due = <Duration expirationDate={endDate ?? 0} isUnpaid={isUnpaid} status={status} />;
      } else if (loan.loanRequest.user && loan.loanRequest.user.id !== userAccount?.id && loan.loanRequest.loanTokenOwner?.ownerAddress !== address && endDate) {
        tableObject.expires = (
          <div className='flex items-center gap-7'>
            <Duration expirationDate={endDate ?? 0} isUnpaid={isUnpaid} />
          </div>
        );
      }

      if (user) {
        tableObject.nettyScore = (
          <div className='flex justify-center'>
            <NettyScore nettyScore={user.nettyScore} />
          </div>
        );

        if (lenderWithPicture) {
          tableObject.lender = <ProfileColumn user={user} />;
        } else {
          tableObject.lender = <p className='text-[10px]/3 lg:text-sm pl-6 font-medium font-space-mono'>{user.name}</p>;
        }

        if (loan.loanRequest && loan.loanRequest.user) {
          tableObject.duration = <p className={cn('w-max mx-auto', baseClass)}>{term} Days</p>;
        }
      }

      if (borrower) {
        tableObject.nettyScore = (
          <div className='flex justify-center'>
            <NettyScore nettyScore={borrower.nettyScore} />
          </div>
        );
        tableObject.borrower = <ProfileColumn user={borrower} />;
      }

      return tableObject;
    });
  }, [data, forcloseLoan, isLent, lenderWithPicture, userAccount?.id, address, displayLTV, onCancel]);

  return (
    <TableUi
      trStyle={trStyle}
      thStyle={thStyle}
      tdStyle={tdStyle}
      enableDefaultSort
      setRowProps={({
        original: {
          collection_id: { status },
        },
      }) => {
        if (status) {
          return {
            className: 'cursor-pointer',
          };
        }

        return {};
      }}
      handleNavigateDetails={({ status, id, tokenId, collectionId }) => {
        if (disableNavigate) return;
        if (status) {
          router.push(`/loans/${id}/active`);
        } else {
          if (tokenId) {
            router.push(`/loans/${tokenId}/offers`);
          }

          if (collectionId) {
            router.push(`/loans/loan-offers/${collectionId}`);
          }
        }
      }}
      columns={columns}
      data={tableData}
      loading={false}
      hoverClass='dark:hover:!bg-jet-black-100 hover:!bg-light-blue-grey border-[2px] h-20 !bg-transparent'
      borderHover='group-hover:first:!border-l-primary group-hover:last:!border-r-primary'
      sortKey={sortKey}
      dirKey={dirKey}
      wrapperStyle={cn(wrapperStyle)}
    />
  );
};
export default MyLoansTabTable;
